import { useState } from 'react';
import { X, AlertTriangle, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DOG_BREED_LABELS, CAT_BREED_LABELS } from '../../data/breeds';

const RELATIONSHIP_LABEL_KEYS: Record<string, string> = {
  Sibling: 'family.relationshipLabel.Sibling',
  Mother: 'family.relationshipLabel.Mother',
  Father: 'family.relationshipLabel.Father',
  'Current Pet': 'family.relationshipLabel.Current Pet',
  Offspring: 'family.relationshipLabel.Offspring',
  'Maternal Grandmother': 'family.relationshipLabel.Maternal Grandmother',
  'Maternal Grandfather': 'family.relationshipLabel.Maternal Grandfather',
  'Paternal Grandmother': 'family.relationshipLabel.Paternal Grandmother',
  'Paternal Grandfather': 'family.relationshipLabel.Paternal Grandfather',
  'Grandmother (Maternal)': 'family.relationshipLabel.Grandmother (Maternal)',
  'Grandfather (Maternal)': 'family.relationshipLabel.Grandfather (Maternal)',
  'Grandmother (Paternal)': 'family.relationshipLabel.Grandmother (Paternal)',
  'Grandfather (Paternal)': 'family.relationshipLabel.Grandfather (Paternal)',
};

interface ManualFamilyMemberData {
  name: string;
  species: string;
  gender: string;
  breed: string;
  birthYear: string;
  color: string;
  breederName: string;
  notes: string;
}

interface ManualFamilyMemberModalProps {
  onClose: () => void;
  onBack: () => void;
  onAdd: (memberData: ManualFamilyMemberData) => void;
  relationship: string;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

const SPECIES_OPTIONS = ['Dog', 'Cat', 'Other'] as const;
const SPECIES_LABEL_KEYS: Record<string, string> = {
  Dog: 'family.manualModal.speciesDog',
  Cat: 'family.manualModal.speciesCat',
  Other: 'family.manualModal.speciesOther',
};

export function ManualFamilyMemberModal({
  onClose,
  onBack,
  onAdd,
  relationship,
}: ManualFamilyMemberModalProps) {
  const { t } = useTranslation();
  const relationshipDisplay =
    (RELATIONSHIP_LABEL_KEYS[relationship] &&
      t(RELATIONSHIP_LABEL_KEYS[relationship])) ||
    relationship;
  const [formData, setFormData] = useState<ManualFamilyMemberData>({
    name: '',
    species: '',
    gender: '',
    breed: '',
    birthYear: '',
    color: '',
    breederName: '',
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  const breeds =
    formData.species === 'Dog'
      ? DOG_BREED_LABELS
      : formData.species === 'Cat'
        ? CAT_BREED_LABELS
        : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('family.manualModal.title')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('family.addMemberModal.adding', {
                  relationship: relationshipDisplay,
                })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-auto p-6"
        >
          <div className="space-y-4">
            {/* Warning Box */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div
                  className="text-sm text-yellow-900 dark:text-yellow-100"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <div className="mb-1" style={{ fontWeight: 600 }}>
                    {t('family.manualModal.ownerAddedEntry')}
                  </div>
                  <div className="text-xs text-yellow-700 dark:text-yellow-300">
                    {t('family.manualModal.ownerAddedDesc')}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 border-l-4 border-gray-300 dark:border-gray-600">
              <h3
                className="text-sm text-gray-700 dark:text-white/90 mb-3"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
              >
                {t('family.manualModal.basicDetailsRequired')}
              </h3>

              {/* Pet Name */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.petName')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t('family.manualModal.petNamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              {/* Species */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.species')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.species}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      species: e.target.value,
                      breed: '',
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">
                    {t('family.manualModal.selectSpecies')}
                  </option>
                  {SPECIES_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {t(SPECIES_LABEL_KEYS[s])}
                    </option>
                  ))}
                </select>
              </div>

              {/* Gender */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.gender')}
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">
                    {t('family.manualModal.selectGenderOptional')}
                  </option>
                  <option value="male">{t('family.manualModal.male')}</option>
                  <option value="female">
                    {t('family.manualModal.female')}
                  </option>
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {t('family.manualModal.genderInferred')}
                </p>
              </div>

              {/* Breed */}
              {formData.species && breeds.length > 0 && (
                <div className="mb-4">
                  <label
                    className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  >
                    {t('family.manualModal.breed')}
                  </label>
                  <select
                    value={formData.breed}
                    onChange={(e) =>
                      setFormData({ ...formData, breed: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <option value="">
                      {t('family.manualModal.selectBreedOptional')}
                    </option>
                    {breeds.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 border-l-4 border-blue-300 dark:border-blue-600">
              <h3
                className="text-sm text-gray-700 dark:text-white/90 mb-3"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
              >
                {t('family.manualModal.optionalDetails')}
              </h3>

              {/* Birth Year */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('family.manualModal.estimatedBirthYear')}
                </label>
                <select
                  value={formData.birthYear}
                  onChange={(e) =>
                    setFormData({ ...formData, birthYear: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">
                    {t('family.manualModal.selectYearOptional')}
                  </option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Color/Markings */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.colorMarkings')}
                </label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  placeholder={t('family.manualModal.colorPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              {/* Breeder Name */}
              <div className="mb-4">
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.breederName')}
                </label>
                <input
                  type="text"
                  value={formData.breederName}
                  onChange={(e) =>
                    setFormData({ ...formData, breederName: e.target.value })
                  }
                  placeholder={t('family.manualModal.breederNamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              {/* Notes */}
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                >
                  {t('family.manualModal.notesHistory')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={3}
                  placeholder={t('family.manualModal.notesPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
              <div
                className="text-sm text-blue-900 dark:text-blue-100"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <div className="mb-1" style={{ fontWeight: 600 }}>
                  💡 {t('family.manualModal.afterAdding')}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  {t('family.manualModal.afterAddingDesc')}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-white/20">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('family.manualModal.back')}
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('family.manualModal.continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
